import React, { useState, useEffect } from 'react';
import { faLinkedinIn } from '@fortawesome/free-brands-svg-icons';
import {
  faCalendar,
  faCalendarDay,
  faCalendarWeek,
  faEnvelope,
  faEnvelopeOpen,
  faHeartCircleBolt,
  faHomeLgAlt,
  faMapMarked,
  faMapMarker,
  faMapMarkerAlt,
  faMarker,
  faMessage,
  faPhone,
  faVenusMars,
  faChevronUp,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash';
import { Card, Col, Container, Row, Button } from 'react-bootstrap';
import { Cell, Pie, PieChart } from 'recharts';
import EllipsisWithTooltip from '../../ui-elements/elipses-overlay/elipsesWithTooltip';
import LanguageProficiencyPanel from './client-right-content-panes/languageProficiencyPanel';
import InterestsPanel from './client-right-content-panes/interestsPanel';
import FamilyDetailsPanel from './client-right-content-panes/familyDetailsPanel';
import FileContentsPanel from './client-right-content-panes/fileContentsPanel';
import { BolnaService } from '../../../services/bolnaService';

export const CVRightContainer = ({ candidateData, scoreRefreshTrigger }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [aiCallScore, setAiCallScore] = useState(null);
  const [aiCallFeedback, setAiCallFeedback] = useState(null);
  const [isLoadingScore, setIsLoadingScore] = useState(false);

  const candidateId = _.get(candidateData, 'candidateId', '');

  // Fetch AI call score when candidateId is available or candidateData changes
  useEffect(() => {
    const fetchAICallScore = async () => {
      if (!candidateId) return;

      setIsLoadingScore(true);
      try {
        const response = await BolnaService.getCandidateProfile(candidateId);
        if (response.data) {
          setAiCallScore(response.data.latestCallScore);
          setAiCallFeedback(response.data.latestCallFeedback);
        }
      } catch (error) {
        console.error('Failed to fetch AI call score:', error);
      } finally {
        setIsLoadingScore(false);
      }
    };

    fetchAICallScore();
  }, [candidateId, candidateData, scoreRefreshTrigger]);

  // Pie chart data for experience
  const data = [
    { name: 'Rating', value: 4 },
    { name: 'RatingLoss', value: 1 },
  ];
  const COLORS = ['#00D47A', '#E0E0E0'];

  // Pie chart data for AI Score (dynamically calculated)
  const aiScoreData = aiCallScore !== null && aiCallScore !== undefined
    ? [
      { name: 'Score', value: aiCallScore },
      { name: 'Remaining', value: 100 - aiCallScore },
    ]
    : [
      { name: 'Score', value: 0 },
      { name: 'Remaining', value: 100 },
    ];
  const AI_SCORE_COLORS = ['#00D47A', '#E0E0E0'];

  const ctcDetails = {
    CTC: _.get(candidateData, 'candidate.compensationDetails.total'),
    Fixed: _.get(candidateData, 'candidate.compensationDetails.fixed'),
    Variable: _.get(candidateData, 'candidate.compensationDetails.variable'),
    Bonus: _.get(candidateData, 'candidate.compensationDetails.bonus'),
    JoiningBonus: _.get(candidateData, 'candidate.compensationDetails.joiningBonus'),
    ESOP: _.get(candidateData, 'candidate.compensationDetails.esop'),
    OtherBenefits: _.get(candidateData, 'candidate.compensationDetails.otherBenefits'),
    Increment1:
      _.get(candidateData, 'candidate.compensationDetails.currentYr1stIncrementPercentage'),
    Increment2:
      _.get(candidateData, 'candidate.compensationDetails.currentYr2ndIncrementPercentage'),
    ExpectedCTC: _.get(candidateData, 'candidate.compensationDetails.expectedCtc'),
    FixedCTCExpectation: _.get(candidateData, 'candidate.compensationDetails.fixedCtcExpectation'),
    AdditionalNotes: _.get(candidateData, 'candidate.compensationDetails.additionalNotes', '-'),
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(parseInt(timestamp));
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Col>
      <Card className="candidateInformation">
        <Card.Body>
          <div className="innerCardContainer container p-2">
            <Row>
              <Col>
                <div style={{ position: 'relative', width: 70, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PieChart width={70} height={80}>
                    <Pie
                      data={aiScoreData}
                      cx={35}
                      cy={40}
                      innerRadius={20}
                      outerRadius={30}
                      fill="#8884d8"
                      paddingAngle={1}
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                    >
                      {aiScoreData.map((entry, index) => (
                        <Cell key={`ai-cell-${index}`} fill={AI_SCORE_COLORS[index % AI_SCORE_COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                  {/* Score in center of chart */}
                  <div style={{
                    position: 'absolute',
                    top: '45px',
                    left: '40px',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#172155'
                  }}>
                    {isLoadingScore ? '...' : (aiCallScore !== null && aiCallScore !== undefined ? aiCallScore : '--')}
                  </div>
                </div>
              </Col>
              <Col>
                <span className="experienceYears">{_.get(candidateData, 'candidate.workExperience.totalYrs', 0)}</span>
                <span className="experienceYearsLabel"> Years</span>
                <br />
                <span className="experienceYearsSubLabel">Experience</span>
              </Col>
            </Row>
            {!showDetails && (
              <Row>
                <Col>
                  <span className="experienceYears">{ctcDetails.CTC}</span>
                  <span className="experienceYearsLabel"> lakhs</span>
                  <br />
                  <span className="experienceYearsSubLabel">CTC</span>
                </Col>
                <Col>
                  <span className="experienceYears">{ctcDetails.Fixed}</span>
                  <span className="experienceYearsLabel"> lakhs</span>
                  <br />
                  <span className="experienceYearsSubLabel">Fixed Income</span>
                </Col>
              </Row>
            )}
            {/* Toggleable CTC Details Section */}
            <Container className="mt-3">
              {showDetails && (
                <Row className="mt-2">
                  <Col>
                    <Row className="mb-1">
                      <Col xs={6} className="left-col">
                      </Col>
                      <Col xs={6} className="right-col">
                        In Lakhs
                      </Col>
                    </Row>

                    {['CTC', 'Fixed', 'Variable', 'Bonus', 'JoiningBonus', 'ESOP', 'OtherBenefits'].map((key) => (
                      <Row className="mb-1" key={key}>
                        <Col xs={6} className="left-col">{key}</Col>
                        <Col xs={6} className="right-col">Rs {ctcDetails[key]}</Col>
                      </Row>
                    ))}

                    <hr></hr>

                    {/* Increments in the Last 2 Years */}
                    <Row className="mt-2">
                      <Col>
                        <h5 className="increment-heading">Increments in the Last 2 Years</h5>
                      </Col>
                    </Row>
                    <Row className="mb-1">
                      <Col xs={6} className="left-col">
                        Current Year 1st Increment %
                      </Col>
                      <Col xs={6} className="right-col">
                        {ctcDetails.Increment1}%
                      </Col>
                    </Row>
                    <Row className="mb-1">
                      <Col xs={6} className="left-col">
                        Current Year 2nd Increment %
                      </Col>
                      <Col xs={6} className="right-col">
                        {ctcDetails.Increment2}%
                      </Col>
                    </Row>
                    <Row className="mb-1">
                      <Col xs={6} className="left-col">Expected CTC P/A</Col>
                      <Col xs={6} className="right-col">Rs {ctcDetails.ExpectedCTC}</Col>
                    </Row>
                    <Row className="mb-1">
                      <Col xs={6} className="left-col">Fixed CTC Expectation P/A</Col>
                      <Col xs={6} className="right-col">Rs {ctcDetails.FixedCTCExpectation}</Col>
                    </Row>
                    <hr></hr>

                    {/* Additional Notes */}
                    <Row className="mt-2">
                      <Col>
                        <h5 className="increment-heading">Additional Notes</h5>
                      </Col>
                    </Row>
                    <Row>
                      <Col className="left-col mb-1">{ctcDetails.AdditionalNotes}</Col>
                    </Row>
                  </Col>
                </Row>
              )}
              <Row>
                <Col className="text-center">
                  <Button variant="link" className="p-0 ctc-toggle-button" onClick={() => setShowDetails(!showDetails)}>
                    {showDetails ? 'View Less' : 'View CTC Details'}{' '}
                    <FontAwesomeIcon icon={showDetails ? faChevronUp : faChevronDown} />
                  </Button>
                </Col>
              </Row>
            </Container>
          </div>

          <Container className="mt-3">
            <Row>
              <Col md={12} className="candidateSubInfoLabels">
                <span className="rounded-grey-background">
                  <FontAwesomeIcon icon={faLinkedinIn} className="rounded-grey-icon" />
                </span>

                {_.get(candidateData, 'candidate.valueAdds.linkedInUrl') ? (
                  <span>
                    <a
                      href={_.get(candidateData, 'candidate.valueAdds.linkedInUrl')}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      {`${_.get(candidateData, 'candidate.personalDetails.firstName', '')} ${_.get(candidateData, 'candidate.personalDetails.lastName', '')}`}
                    </a>
                  </span>
                ) : '-'}

              </Col>

              <Col md={12} className="candidateSubInfoLabels">
                <span className="rounded-grey-background">
                  <FontAwesomeIcon icon={faPhone} className="rounded-grey-icon" />
                </span>
                <span> {_.get(candidateData, 'candidate.personalDetails.primaryPhoneNumber', '-')}</span>
              </Col>
              <Col md={12} className="candidateSubInfoLabels">
                <span className="rounded-grey-background">
                  <FontAwesomeIcon icon={faEnvelope} className="rounded-grey-icon" />
                </span>
                <span> {_.get(candidateData, 'candidate.personalDetails.primaryEmailId', '-')}</span>
              </Col>
              <Col md={12} className="candidateSubInfoLabels">
                <span className="rounded-grey-background">
                  <FontAwesomeIcon icon={faCalendar} className="rounded-grey-icon" />
                </span>{' '}
                <span>
                  {formatDate(_.get(candidateData, 'candidate.personalDetails.dateOfBirth'))}
                </span>
              </Col>
              <Col md={12} className="candidateSubInfoLabels">
                <span className="rounded-grey-background">
                  <FontAwesomeIcon icon={faVenusMars} className="rounded-grey-icon" />
                </span>
                <span> {_.get(candidateData, 'candidate.personalDetails.gender', '-')}</span>
              </Col>
              <Col md={12} className="candidateSubInfoLabels">
                <span className="rounded-grey-background">
                  <FontAwesomeIcon icon={faHeartCircleBolt} className="rounded-grey-icon" />
                </span>
                <span> {_.get(candidateData, 'candidate.personalDetails.maritalStatus', '-')}</span>
              </Col>
              <Col md={12} className="candidateSubInfoLabels">
                <span className="rounded-grey-background">
                  <FontAwesomeIcon icon={faCalendarWeek} className="rounded-grey-icon" />
                </span>

                <span>
                  Notice Period: {_.get(candidateData, 'candidate.workExperience.currentRole.noticePeriod', '-')}{' '}
                  {_.get(candidateData, 'candidate.workExperience.currentRole.noticePeriod') && ' Days'}
                </span>
              </Col>
              <Col md={12} className="candidateSubInfoLabels">
                <span className="rounded-grey-background">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="rounded-grey-icon" />
                </span>

                <span> Native City: {_.get(candidateData, 'candidate.valueAdds.nativeLocation', '-')}</span>
              </Col>
              <Col md={12} className="candidateSubInfoLabels1 d-flex align-items-start">
                <span className="rounded-grey-background d-flex align-items-center justify-content-center">
                  <FontAwesomeIcon icon={faHomeLgAlt} className="rounded-grey-icon" />
                </span>

                <span className="address-text">
                  Current Address: {[
                    _.get(candidateData, 'candidate.personalDetails.currentAddress.address1', ''),
                    _.get(candidateData, 'candidate.personalDetails.currentAddress.address2', ''),
                    _.get(candidateData, 'candidate.personalDetails.currentAddress.city', ''),
                    _.get(candidateData, 'candidate.personalDetails.currentAddress.state', ''),
                    _.get(candidateData, 'candidate.personalDetails.currentAddress.country', '')
                  ].filter(Boolean).join(' ')}
                </span>
              </Col>

            </Row>
          </Container>
        </Card.Body>
      </Card>
      <LanguageProficiencyPanel
        languageProficiency={_.get(candidateData, 'candidate.valueAdds.languageProficiencies', [])}
      />
      <InterestsPanel interests={_.get(candidateData, 'candidate.valueAdds.interests', [])} />

      <FamilyDetailsPanel
        familyDetails={{
          ..._.get(candidateData, 'candidate.valueAdds', {}),
          maritalStatus: _.get(candidateData, 'candidate.personalDetails.maritalStatus', '-'),
        }}
      />

      <FileContentsPanel
        title="Documents"
        fileList={_.get(candidateData, 'candidate.educationalDetails.educationalDocs', [])}
      />

      <FileContentsPanel
        title="DISC Assessment Report"
        fileList={_.get(candidateData, 'candidate.educationalDetails.assessmentFiles', [])}
      />

      <FileContentsPanel
        title="Additional Information"
        fileList={_.get(candidateData, 'candidate.valueAdds.additionalInfo', [])}
      />
    </Col>
  );
};